import React, { useState } from "react";
import { Button, TextField } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import InputComponent from "../InputComponent";
import { useAuth } from "../../contexts/AuthContext";
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { showCustomToast } from "../Misc/CustomToast";
import DropDownComponentSorted from "../DropDownComponentSorted";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import "./EditGroupNotesRedesign.css";

function EditGroupNote(props) {
  const { currentUser } = useAuth();

  const [typeGroupNote, setTypeGroupNote] = useState(props.groupType);
  const [otherTypeGroupNote, setOtherTypeGroupNote] = useState(
    props.otherTypeGroupNote
  );
  const [groupTopic, setGroupTopic] = useState(props.groupTopic);
  const [noteContent, setNoteContent] = useState(props.noteContent);
  const [notInAttendance, setNotInAttendance] = useState(props.notInAttendance);
  const [hasChanged, setHasChanged] = useState(false);
  const [groupNoteDateTime, setGroupNoteDateTime] = useState(
    props.effectiveDateTime || ""
  );
  const [attendees, setAttendees] = useState(props.attendees || []);
  const [inhouseClients, setInhouseClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleTypeGroupNote = (event) => {
    setTypeGroupNote(event);
    setHasChanged(true);
  };

  const handleGroupTopic = (event) => {
    setGroupTopic(event);
    setHasChanged(true);
  };

  const handleOtherTypeGroupNote = (event) => {
    setOtherTypeGroupNote(event);
    setHasChanged(true);
  };

  const handleNoteContent = (event) => {
    setNoteContent(event.target.value);
    setHasChanged(true);
  };

  const handleNotInAttendance = (event) => {
    setNotInAttendance(event.target.value);
    setHasChanged(true);
  };

  const handleDateTimeChange = (event) => {
    setGroupNoteDateTime(event);
    setHasChanged(true);
  };

  const handleToggleAttendee = (clientId) => {
    setAttendees((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
    setHasChanged(true);
  };

  React.useEffect(() => {
    const fetchInhouse = async () => {
      setIsLoadingClients(true);
      try {
        const q = query(
          collection(db, "clients"),
          where("clientStatus", "==", "In House")
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setInhouseClients(data);
      } catch (e) {
        console.error("Failed to load in-house clients for edit group note", e);
      } finally {
        setIsLoadingClients(false);
      }
    };
    fetchInhouse();
  }, []);

  const handleUpdateGroupNote = async (note, effectiveDateTime) => {
    const attendeesDetails = attendees
      .map((id) => {
        const client = inhouseClients.find((c) => c.id === id);
        const name = `${client?.preferredName || client?.firstName || ""} ${
          client?.lastName || ""
        }`
          .trim()
          .replace(/\s+/g, " ");
        return {
          id,
          name: name || "Unnamed client",
        };
      })
      .filter(Boolean);

    const noteData = {
      noteType: "groupNote",
      noteContent: note,
      updateAt: serverTimestamp(),
      effectiveDateTime: effectiveDateTime,
      doneBy: currentUser.displayName,
      notInAttendance: notInAttendance,
      groupTopic: groupTopic,
      typeGroupNote: typeGroupNote,
      otherTypeGroupNote: otherTypeGroupNote,
      attendees,
      attendeesDetails,
    };

    if (typeGroupNote && groupTopic && noteContent && notInAttendance) {
      setIsLoading(true);
      try {
        const docRef = doc(db, "notes", props.noteId);
        await updateDoc(docRef, noteData);
        showCustomToast("Group note successfully updated!", "success");

        setIsLoading(false);
        props.closemodal();
      } catch (error) {
        console.log("error updating note :", error);
        showCustomToast("Problem when updating note!", "error");
      }
    } else {
      showCustomToast(
        "Some mandatory fields are empty. Please check.!",
        "error"
      );
    }
  };

  const handleSubmit = () => {
    handleUpdateGroupNote(noteContent, groupNoteDateTime);
  };

  return (
    <div className="edit-group-note-modal">
      <div className="edit-group-note-header">
        <div>
          <h2>Edit group note</h2>
          <p>Update the session details, attendance, and clinical note.</p>
        </div>
        <button
          type="button"
          className="edit-group-note-close"
          onClick={props.closemodal}
          aria-label="Close edit group note modal"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="edit-group-note-body">
        <section className="edit-group-note-section">
          <div className="edit-group-note-section-title">
            <span>Session details</span>
          </div>

          <div className="edit-group-note-grid">
            <div className="edit-group-note-field">
              <InputComponent
                isRequired={true}
                title="Group topic"
                width="100%"
                value={groupTopic}
                onChange={handleGroupTopic}
              />
            </div>

            <div className="edit-group-note-field">
              <DatePickerWithTimeComponent
                isRequired={true}
                value={groupNoteDateTime}
                onChange={handleDateTimeChange}
                title="Group Date and start time"
              />
            </div>

            <div className="edit-group-note-field">
              <DropDownComponentSorted
                isRequired={true}
                api="groupType"
                title="Type of group"
                value={typeGroupNote}
                onChange={handleTypeGroupNote}
              />
            </div>

            {typeGroupNote === "Other" && (
              <div className="edit-group-note-field">
                <InputComponent
                  title="If other, describe"
                  width="100%"
                  value={otherTypeGroupNote}
                  onChange={handleOtherTypeGroupNote}
                />
              </div>
            )}
          </div>
        </section>

        <section className="edit-group-note-section">
          <div className="edit-group-note-section-title">
            <span>Attendance</span>
            <strong>
              {attendees.length ? `${attendees.length} selected` : "Optional"}
            </strong>
          </div>

          <div className="edit-group-note-attendance-list">
            {isLoadingClients ? (
              <div className="edit-group-note-empty-state">
                Loading in-house clients...
              </div>
            ) : inhouseClients.length === 0 ? (
              <div className="edit-group-note-empty-state">
                No in-house clients found.
              </div>
            ) : (
              inhouseClients.map((client) => {
                const name = `${client?.preferredName || client?.firstName || ""} ${
                  client?.lastName || ""
                }`
                  .trim()
                  .replace(/\s+/g, " ");
                return (
                  <FormControlLabel
                    key={client.id}
                    className="edit-group-note-attendee"
                    control={
                      <Checkbox
                        size="small"
                        checked={attendees.includes(client.id)}
                        onChange={() => handleToggleAttendee(client.id)}
                      />
                    }
                    label={name || "Unnamed client"}
                    sx={{ margin: 0 }}
                  />
                );
              })
            )}
          </div>
        </section>

        <section className="edit-group-note-section">
          <div className="edit-group-note-section-title">
            <span>Clinical note</span>
          </div>

          <div className="edit-group-note-textarea">
            <div className="edit-group-note-label">
              Group note <span>*</span>
            </div>
            <TextField
              placeholder="Type group note here"
              multiline
              rows={9}
              value={noteContent}
              onChange={handleNoteContent}
              fullWidth
            />
          </div>

          <div className="edit-group-note-textarea">
            <div className="edit-group-note-label">
              Clients not in attendance <span>*</span>
            </div>
            <TextField
              placeholder="Type names here and the reason why the client is not in group, if known..."
              multiline
              rows={4}
              value={notInAttendance}
              onChange={handleNotInAttendance}
              fullWidth
            />
          </div>
        </section>
      </div>

      <div className="edit-group-note-footer">
        <Button
          variant="outlined"
          className="edit-group-note-cancel"
          onClick={props.closemodal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="edit-group-note-save"
          disabled={!hasChanged || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? "Updating..." : "Update note"}
        </Button>
      </div>
    </div>
  );
}

export default EditGroupNote;
